import axios from "axios";

const BACKEND_URL = process.env.NODE_ENV === "production"
  ? (process.env.REACT_APP_BACKEND_URL || "")
  : (process.env.REACT_APP_BACKEND_URL || "http://localhost:5001");
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({ 
  baseURL: API,
  withCredentials: true
});

// Attach Authorization header if token exists in localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("alaira_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Bypass-Tunnel-Reminder"] = "true";
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Map backend product representation to frontend expected model
export const mapProduct = (p) => {
  if (!p || typeof p !== "object") return null;
  const id = p._id || p.id || String(Math.random());
  const image = p.mainImage?.url || (Array.isArray(p.images) ? (typeof p.images[0] === 'string' ? p.images[0] : p.images[0]?.url) : (typeof p.image === 'string' ? p.image : '')) || "";
  const rawImages = Array.isArray(p.images) ? p.images : (p.image ? [p.image] : []);
  const images = rawImages.map(img => typeof img === 'string' ? img : (img?.url || '')).filter(Boolean);
  if (images.length === 0 && image) images.push(image);
  const original_price = p.price || 999;
  const price = p.discountPrice || p.price || 799;
  const discount_pct = original_price > price ? Math.round(((original_price - price) / original_price) * 100) : 0;
  const on_sale = discount_pct > 0;
  const reviews_count = Array.isArray(p.reviews) ? p.reviews.length : (typeof p.reviews_count === "number" ? p.reviews_count : 0);
  const name = p.title || p.name || "Special Gift";
  const rating = p.ratings?.average || p.rating || 4.9;
  
  const rawReviews = Array.isArray(p.reviews) ? p.reviews : [];
  const reviews = rawReviews.map(r => {
    if (!r) return null;
    const author = r.user?.name || "Anonymous";
    const rawAvatar = r.user?.avatar || "";
    const avatar = (rawAvatar && (rawAvatar.startsWith("http") || rawAvatar.startsWith("/")))
      ? rawAvatar
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=1C1924&color=fff&rounded=true&bold=true`;
    return {
      author,
      avatar,
      rating: r.rating || 5,
      comment: r.comment || "",
      body: r.comment || "",
      date: r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Recently"
    };
  }).filter(Boolean);
  
  const videos = p.videos?.map(v => typeof v === 'string' ? v : v.url) || [];
  const declaration = p.declaration || "Certified Authentic Alaira Premium Quality Gift.";
  const shippingReturns = p.shippingReturns || "Ships within 24-48 hours. Returns accepted within 14 days under original wrapper conditions.";
  const faqs = p.faqs && p.faqs.length ? p.faqs : [
    { question: "Is this wrapped ready for gifting?", answer: "Yes, every Alaira order comes with our signature presentation wrap and a blank matching greeting card." }
  ];

  return {
    id,
    name,
    title: name,
    price,
    original_price,
    discount_pct,
    on_sale,
    image,
    images,
    videos,
    category: p.category,
    description: p.description,
    declaration,
    shippingReturns,
    faqs,
    fabric: p.fabric || "Soft Cotton",
    occasion: p.occasion || "Anniversary",
    color: p.color || "Multi",
    stock: p.stock || 10,
    rating,
    reviews_count,
    reviews,
    delivery_days: p.delivery_days || "3-5 days",
    badge: p.featured ? "Featured" : "",
    highlights: p.highlights || [
      "Premium luxury materials",
      "Handcrafted with love",
      "Signature presentation wrapping included"
    ]
  };
};

export const DEFAULT_CATEGORIES = [
  { name: "Toys", slug: "toys", image: "/toys/IMG_4912.PNG", count: 20, tint: "#F4F0FF" },
  { name: "Flowers", slug: "flowers", image: "/flowers/IMG_4930.PNG", count: 12, tint: "#EAF5FF" },
  { name: "Sweets", slug: "sweets", image: "/sweets/IMG_4057.JPG.jpeg", count: 44, tint: "#FFF0F3" }
];

export const getCategories = () => 
  api.get("/categories")
    .then((r) => {
      const data = (r.data || []).filter(c => c.slug !== "jewelry" && c.name?.toLowerCase() !== "jewelry");
      const hasSweets = data.some(c => c.slug === "sweets" || c.name?.toLowerCase() === "sweets" || c.name?.toLowerCase() === "chocolates");
      let list = [...data];
      if (!hasSweets) {
        list.push({ name: "Sweets", slug: "sweets", image: "/sweets/IMG_4057.JPG.jpeg", count: 44, tint: "#FFF0F3" });
      }
      return list;
    })
    .catch(() => DEFAULT_CATEGORIES);

export const getProducts = (params = {}) => {
  return api.get("/products", { params })
    .then((r) => {
      const raw = Array.isArray(r.data) ? r.data : (r.data?.products || []);
      return raw.map(mapProduct).filter(Boolean);
    })
    .catch((err) => {
      console.warn("Failed to fetch products from backend API:", err);
      return [];
    });
};

export const getAdminProducts = (params = {}) => {
  return api.get("/products", { params })
    .then((r) => {
      const raw = Array.isArray(r.data) ? r.data : (r.data?.products || []);
      return raw;
    })
    .catch((err) => {
      console.warn("Failed to fetch admin products from backend API:", err);
      return [];
    });
};

export const getProduct = (id) => {
  return api.get(`/products/${id}`)
    .then((r) => {
      const p = r.data.product || r.data;
      if (p) return mapProduct(p);
      return null;
    })
    .catch(() => null);
};

export const DEFAULT_COMBOS = [
  {
    id: "combo1",
    name: "Sweet Comfort Flowers & Plush",
    image: "/toys/IMG_4912.PNG",
    ribbon: "Cozy Choice",
    savings_pct: 22,
    tagline: "FLOWERS + TOYS",
    included: [
      "Grand Celebration Flower Basket",
      "Blush Pink Fluffy Pillow Bear"
    ],
    original_price: 4498,
    price: 3499
  },
  {
    id: "combo4",
    name: "Ultimate Couple Sweet Romance Hamper",
    image: "/sweets/IMG_4057.JPG.jpeg",
    ribbon: "Couple Favorite",
    savings_pct: 22,
    tagline: "CHOCOLATES + FLOWERS + TOYS",
    included: [
      "Cadbury Silk Heart Blush Romantic Edition",
      "Crimson Velvet Rose Bouquet",
      "Calming Lavender Plush Bear"
    ],
    original_price: 7999,
    price: 5999
  },
  {
    id: "combo6",
    name: "Sweet Moments Plush & Choco Box",
    image: "/sweets/IMG_4054.JPG.jpeg",
    ribbon: "Sweet Romance",
    savings_pct: 21,
    tagline: "CHOCOLATES + TOYS",
    included: [
      "KitKat Luxury Gift Box Collection",
      "Twin Magnetic Love Pandas"
    ],
    original_price: 3799,
    price: 2999
  },
  {
    id: "combo7",
    name: "Royal Makhana & Truffle Gourmet Gift Hamper",
    image: "/healthy-sweets/image13.jpeg",
    ribbon: "Healthy Gourmet",
    savings_pct: 25,
    tagline: "HEALTHY SWEETS + TRUFFLE MAKHANA",
    included: [
      "MAKHANA Royal White & Purple Luxury Box",
      "Matte Black Truffle Roasted Makhana Canister",
      "Pateo Gur Makhana Jaggery Caramelized Bites"
    ],
    original_price: 3997,
    price: 2999
  },
  {
    id: "combo8",
    name: "Heritage Wooden Keepsake Makhana & Cookie Feast",
    image: "/healthy-sweets/image50.jpeg",
    ribbon: "Luxury Treasure",
    savings_pct: 28,
    tagline: "HEALTHY SWEETS + COOKIE CRAFT",
    included: [
      "Heritage Teakwood Gourmet Hamper Chest",
      "MAKHANA Gourmet Flavored Fox Nuts Trio Pack",
      "Cookie Craft Choco Chip Oat Display Canister"
    ],
    original_price: 4997,
    price: 3599
  }
];

export const getCombos = () => api.get("/combos").then((r) => r.data).catch(() => []);
export const createCombo = (payload) => api.post("/combos", payload).then((r) => r.data);
export const updateCombo = (id, payload) => api.put(`/combos/${id}`, payload).then((r) => r.data);
export const deleteCombo = (id) => api.delete(`/combos/${id}`).then((r) => r.data);

export const createOrder = (payload) => api.post("/orders", payload).then((r) => r.data);

export const subscribeNewsletter = (email) => api.post("/newsletter", { email }).then((r) => r.data);
export const submitEnquiry = (payload) => api.post("/enquiries", payload).then((r) => r.data);

// Authentication actions
export const loginUser = (payload) => 
  api.post("/users/login", payload).then((r) => {
    if (r.data?.token) {
      localStorage.setItem("alaira_token", r.data.token);
    }
    return r.data;
  });

export const registerUser = (payload) => 
  api.post("/users/register", payload).then((r) => {
    if (r.data?.token) {
      localStorage.setItem("alaira_token", r.data.token);
    }
    return r.data;
  });

export const getCurrentUser = () => 
  api.get("/users/me").then((r) => r.data);

export const logoutUser = () => 
  api.post("/users/logout").then((r) => {
    localStorage.removeItem("alaira_token");
    return r.data;
  }).catch((err) => {
    localStorage.removeItem("alaira_token");
    throw err;
  });

export const submitProductReview = (id, payload) => 
  api.post(`/products/${id}/reviews`, payload).then((r) => {
    return {
      ratings: r.data.ratings,
      reviews: r.data.reviews?.map(rv => ({
        author: rv.user?.name || "Anonymous",
        avatar: rv.user?.avatar || "",
        rating: rv.rating || 5,
        comment: rv.comment || "",
        body: rv.comment || "",
        date: rv.createdAt ? new Date(rv.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Recently"
      })) || []
    };
  });

export const getMyOrders = () => 
  api.get("/orders/me").then((r) => r.data.orders || r.data);

export const loginWithFirebaseToken = (idToken) => 
  api.post("/users/auth/firebase", {}, {
    headers: { Authorization: `Bearer ${idToken}` }
  }).then((r) => {
    if (r.data?.token) {
      localStorage.setItem("alaira_token", r.data.token);
    }
    return r.data;
  });

// Admin Panel endpoints
export const getAdminStats = () => api.get("/admin/stats").then((r) => r.data);
export const getAdminEnquiries = () => api.get("/enquiries").then((r) => r.data.enquiries || []).catch(() => []);
export const updateEnquiryStatus = (id, status) => api.patch(`/enquiries/${id}/status`, { status }).then((r) => r.data);
export const deleteEnquiry = (id) => api.delete(`/enquiries/${id}`).then((r) => r.data);
export const getAdminOrders = () => api.get("/orders").then((r) => Array.isArray(r.data?.orders) ? r.data.orders : (Array.isArray(r.data) ? r.data : [])).catch(() => []);
export const updateOrderStatus = (id, deliveryStatus) => api.patch(`/orders/${id}/status`, { deliveryStatus }).then((r) => r.data);
export const getCoupons = () => api.get("/coupons").then((r) => Array.isArray(r.data?.coupons) ? r.data.coupons : (Array.isArray(r.data) ? r.data : [])).catch(() => []);
export const createCoupon = (payload) => api.post("/coupons", payload).then((r) => r.data);
export const deleteCoupon = (id) => api.delete(`/coupons/${id}`).then((r) => r.data);
export const createProduct = (payload) => api.post("/products", payload).then((r) => r.data);
export const updateProduct = (id, payload) => api.put(`/products/${id}`, payload).then((r) => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then((r) => r.data);


